import {
  createSandboxEntitlement,
  createSandboxEntryLink,
  deleteSandboxEntitlement,
  listSandboxEntitlements,
  listSandboxPlatforms,
  SandboxEntitlement,
  SandboxEntitlementRequest,
  SandboxPlatform,
  updateSandboxEntitlement,
} from '../../api/benefitPlatformSandboxApi'
import NavBar from '../../components/nav-bar/nav-bar'
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'

type EntitlementFormValues = Omit<SandboxEntitlementRequest, 'expiresAt'> & {
  expiresAt?: string
}

const toLocalDateTimeInput = (value: string) => {
  const date = new Date(value)
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

const BenefitPlatformSandbox = () => {
  const [platforms, setPlatforms] = useState<SandboxPlatform[]>([])
  const [entitlements, setEntitlements] = useState<SandboxEntitlement[]>([])
  const [entitlementEditor, setEntitlementEditor] = useState<SandboxEntitlement | 'new' | null>(
    null,
  )
  const [entitlementForm] = Form.useForm<EntitlementFormValues>()
  const [loading, setLoading] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [nextPlatforms, nextEntitlements] = await Promise.all([
        listSandboxPlatforms(),
        listSandboxEntitlements(),
      ])
      setPlatforms(nextPlatforms)
      setEntitlements(nextEntitlements)
    } catch (error: any) {
      notification.error({ message: '沙箱数据加载失败', description: error?.message })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  const enterH5 = async (platform: SandboxPlatform) => {
    const tab = window.open('about:blank', '_blank')
    if (tab) {
      tab.opener = null
    }
    try {
      const link = await createSandboxEntryLink(platform.id)
      if (tab) {
        tab.location.href = link.url
      } else {
        notification.warning({ message: '请允许弹出窗口后重试' })
      }
    } catch (error: any) {
      tab?.close()
      notification.error({ message: '生成 H5 入口失败', description: error?.message })
    }
  }

  const openEntitlementEditor = (entitlement: SandboxEntitlement | 'new') => {
    entitlementForm.resetFields()
    entitlementForm.setFieldsValue(
      entitlement === 'new'
        ? {
            entitlementKey: 'BOOKING',
            availableQuantity: 1,
            enabled: true,
            benefitPlatformId: platforms[0]?.id,
          }
        : {
            ...entitlement,
            expiresAt: entitlement.expiresAt
              ? toLocalDateTimeInput(entitlement.expiresAt)
              : undefined,
          },
    )
    setEntitlementEditor(entitlement)
  }

  const saveEntitlement = async () => {
    const values = await entitlementForm.validateFields()
    const request: SandboxEntitlementRequest = {
      ...values,
      expiresAt: values.expiresAt ? new Date(values.expiresAt).toISOString() : null,
    }
    try {
      if (entitlementEditor === 'new') {
        await createSandboxEntitlement(request)
      } else if (entitlementEditor) {
        await updateSandboxEntitlement(entitlementEditor.id, request)
      }
      setEntitlementEditor(null)
      await reload()
      notification.success({ message: '测试权益已保存' })
    } catch (error: any) {
      notification.error({ message: '权益保存失败', description: error?.message })
    }
  }

  const removeEntitlement = (entitlement: SandboxEntitlement) => {
    Modal.confirm({
      title: '删除测试权益？',
      content: '已有锁定流水的权益不能删除，请改为禁用。',
      onOk: async () => {
        await deleteSandboxEntitlement(entitlement.id)
        await reload()
      },
    })
  }

  return (
    <Space className="dashboard-wrapper" orientation="vertical">
      <NavBar title="权益平台沙箱" />
      <Card>
        <Tabs
          items={[
            {
              key: 'platforms',
              label: '测试平台',
              children: (
                <>
                  <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={platforms}
                    pagination={false}
                    columns={[
                      { title: '平台', dataIndex: 'name' },
                      { title: 'Code', dataIndex: 'code' },
                      { title: 'Client ID', dataIndex: 'clientId' },
                      {
                        title: '状态',
                        dataIndex: 'platformEnabled',
                        render: (_: boolean, platform: SandboxPlatform) => (
                          <Tag
                            color={
                              platform.platformEnabled ? 'green' : 'default'
                            }
                          >
                            {platform.platformEnabled ? '启用' : '停用'}
                          </Tag>
                        ),
                      },
                      {
                        title: '操作',
                        render: (_: unknown, platform: SandboxPlatform) => (
                          <Space>
                            <Button
                              type="primary"
                              disabled={!platform.platformEnabled}
                              onClick={() => enterH5(platform)}
                            >
                              进入 H5
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'entitlements',
              label: '测试权益',
              children: (
                <>
                  <Button
                    type="primary"
                    disabled={platforms.length === 0}
                    onClick={() => openEntitlementEditor('new')}
                  >
                    新增权益
                  </Button>
                  <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={entitlements}
                    pagination={{ pageSize: 20 }}
                    columns={[
                      {
                        title: '平台',
                        dataIndex: 'benefitPlatformId',
                        render: (id: number) =>
                          platforms.find(platform => platform.id === id)?.name ?? id,
                      },
                      { title: '手机号', dataIndex: 'phoneNumber' },
                      { title: '权益类型', dataIndex: 'entitlementKey' },
                      { title: '可用数量', dataIndex: 'availableQuantity' },
                      {
                        title: '有效期',
                        dataIndex: 'expiresAt',
                        render: (value: string | null) =>
                          value ? new Date(value).toLocaleString() : '不限',
                      },
                      {
                        title: '状态',
                        dataIndex: 'enabled',
                        render: (enabled: boolean) => (enabled ? '启用' : '停用'),
                      },
                      {
                        title: '操作',
                        render: (_: unknown, entitlement: SandboxEntitlement) => (
                          <Space>
                            <Button onClick={() => openEntitlementEditor(entitlement)}>编辑</Button>
                            <Button danger onClick={() => removeEntitlement(entitlement)}>
                              删除
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={entitlementEditor === 'new' ? '新增测试权益' : '编辑测试权益'}
        open={entitlementEditor !== null}
        onCancel={() => setEntitlementEditor(null)}
        onOk={saveEntitlement}
        destroyOnHidden
      >
        <Form form={entitlementForm} layout="vertical">
          <Form.Item name="benefitPlatformId" label="平台" rules={[{ required: true }]}>
            <Select
              disabled={entitlementEditor !== 'new'}
              options={platforms.map(platform => ({ label: platform.name, value: platform.id }))}
            />
          </Form.Item>
          <Form.Item name="phoneNumber" label="手机号" rules={[{ required: true }]}>
            <Input disabled={entitlementEditor !== 'new'} />
          </Form.Item>
          <Form.Item name="entitlementKey" label="权益类型" rules={[{ required: true }]}>
            <Input disabled={entitlementEditor !== 'new'} />
          </Form.Item>
          <Form.Item name="availableQuantity" label="可用数量" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="expiresAt" label="有效期">
            <Input type="datetime-local" />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}

export default BenefitPlatformSandbox
